import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LiveActionZones, {
  LIVE_ACTION_DEFINITIONS,
  type LiveActionDefinition,
} from '@/components/live/LiveActionZones';
import { formatMatchClock } from '@/types';
import { LIVE_PERIOD_LABELS, type LivePeriod } from '@/utils/liveClock';
import { getJudgementLabel, type LiveJudgementChoice } from '@/components/live/LiveJudgementBar';

interface Props {
  open: boolean;
  judgement: LiveJudgementChoice | null;
  /** Match clock frozen when step 1 was clicked. */
  snapshotMatchSecond: number;
  /** Current live match clock (updates while dialog is open). */
  liveMatchSecond: number;
  snapshotPeriod?: string | null;
  livePeriod?: string | null;
  clockRunning?: boolean;
  onClose: () => void;
  onSubmit: (actions: LiveActionDefinition[]) => void;
  submitting?: boolean;
}

function periodLabel(period?: string | null): string | null {
  if (!period) return null;
  return LIVE_PERIOD_LABELS[period as LivePeriod] || period;
}

export default function LiveEventPickDialog({
  open,
  judgement,
  snapshotMatchSecond,
  liveMatchSecond,
  snapshotPeriod,
  livePeriod,
  clockRunning = false,
  onClose,
  onSubmit,
  submitting = false,
}: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      setSelectedKeys(new Set());
    }
  }, [open, judgement]);

  const selectedActions = useMemo(
    () => LIVE_ACTION_DEFINITIONS.filter((action) => selectedKeys.has(action.key)),
    [selectedKeys]
  );

  const toggleAction = (action: LiveActionDefinition) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(action.key)) next.delete(action.key);
      else next.add(action.key);
      return next;
    });
  };

  const markPeriod = periodLabel(snapshotPeriod);
  const showLiveClock = clockRunning && liveMatchSecond !== snapshotMatchSecond;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      scroll="paper"
      sx={{ zIndex: (t) => t.zIndex.modal + 20 }}
    >
      <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          步驟 2 · 選擇事件
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
          {judgement && (
            <Chip size="small" color="secondary" label={`判決：${getJudgementLabel(judgement)}`} />
          )}
          <Chip
            size="small"
            color="secondary"
            variant="outlined"
            label={`標記時間 ${formatMatchClock(snapshotMatchSecond)}`}
          />
          {markPeriod && (
            <Chip size="small" variant="outlined" label={markPeriod} />
          )}
          {clockRunning && (
            <Chip
              size="small"
              variant="outlined"
              label={`現場 ${formatMatchClock(liveMatchSecond)}`}
              sx={
                showLiveClock
                  ? { borderStyle: 'dashed', color: 'text.secondary' }
                  : undefined
              }
            />
          )}
          {clockRunning && showLiveClock && periodLabel(livePeriod) && livePeriod !== snapshotPeriod && (
            <Chip size="small" variant="outlined" label={periodLabel(livePeriod)!} />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={`已選 ${selectedKeys.size} 項`}
            color={selectedKeys.size > 0 ? 'secondary' : 'default'}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          標記時間為步驟 1 按下時的快照；可同時選擇多個事件，同一時間點一次提交
        </Typography>
        <IconButton
          aria-label="關閉"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <LiveActionZones
          multiSelect
          selectedKeys={selectedKeys}
          onActionTap={toggleAction}
          largeTouch
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" fullWidth={fullScreen} disabled={submitting}>
          取消
        </Button>
        <Button
          onClick={() => onSubmit(selectedActions)}
          variant="contained"
          color="secondary"
          fullWidth={fullScreen}
          disabled={selectedActions.length === 0 || submitting}
        >
          {submitting ? '提交中…' : `提交 (${selectedActions.length}) · ${formatMatchClock(snapshotMatchSecond)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
